import { env } from '../config/env'

import {
  RealtimeClient,
  type RealtimeChannel,
  type RealtimeSocketFactory,
  type RealtimeStatus,
  type ServerFrame,
} from './client'

export type RealtimeFrameListener = (frame: ServerFrame) => void
export type RealtimeStatusListener = (status: RealtimeStatus) => void
export type RealtimeResyncListener = () => void

export interface RealtimeManagerOptions {
  url?: string
  getAccessToken?: () => string | null
  socketFactory?: RealtimeSocketFactory
  /** Test/deployment escape hatch; defaults to enabled outside unit tests. */
  enabled?: boolean
  reconnectBaseMs?: number
  reconnectMaxMs?: number
  authTimeoutMs?: number
  heartbeatMs?: number
  random?: () => number
}

const MAX_TRACKED_NOTIFICATIONS = 100

function isTestEnvironment(): boolean {
  return import.meta.env.MODE === 'test'
}

/**
 * A manager is enabled for every authenticated client. The default instance is
 * disabled under Vitest unless a socket factory or explicit URL is provided,
 * so unit tests never open a real network socket. Production and development
 * builds always connect to the same-origin `/ws` proxy unless `VITE_WS_URL`
 * points at the native API host.
 */
function resolveEnabled(options: RealtimeManagerOptions): boolean {
  if (typeof options.enabled === 'boolean') return options.enabled
  if (options.socketFactory) return true
  if (env.wsUrl) return true
  return !isTestEnvironment()
}

/**
 * App-level realtime state. It owns exactly one `RealtimeClient` for the
 * signed-in session and fans frames/status to feature hooks. Server data stays
 * in the routes (D-042): this object only carries connection state, channel
 * reference counts and a small notification-id dedupe cache.
 */
export class RealtimeManager {
  private readonly options: RealtimeManagerOptions
  private readonly enabled: boolean
  private client: RealtimeClient | null = null
  private status: RealtimeStatus = 'idle'
  private started = false
  private hasConnected = false
  private readyUserId: string | null = null

  private readonly frameListeners = new Set<RealtimeFrameListener>()
  private readonly statusListeners = new Set<RealtimeStatusListener>()
  private readonly resyncListeners = new Set<RealtimeResyncListener>()
  private readonly channels = new Map<
    string,
    { channel: RealtimeChannel; id: string; count: number }
  >()
  private readonly seenNotificationIds = new Set<string>()
  private notificationOrder: string[] = []

  constructor(options: RealtimeManagerOptions = {}) {
    this.options = options
    this.enabled = resolveEnabled(options)
  }

  isEnabled(): boolean {
    return this.enabled
  }

  getStatus(): RealtimeStatus {
    return this.status
  }

  getUserId(): string | null {
    return this.readyUserId
  }

  start(): void {
    if (!this.enabled || this.started) return
    if (!this.getAccessToken()) return

    if (this.status === 'closed' || this.status === 'idle') {
      this.hasConnected = false
    }
    this.started = true
    const client = this.ensureClient()
    for (const subscription of this.channels.values()) {
      client.subscribe(subscription.channel, subscription.id)
    }
    client.connect()
  }

  stop(): void {
    const hadClient = this.client !== null
    this.started = false
    this.hasConnected = false
    this.readyUserId = null
    this.channels.clear()
    this.seenNotificationIds.clear()
    this.notificationOrder = []
    if (hadClient) {
      this.client?.disconnect({ clearSubscriptions: true })
    } else {
      this.setStatus('closed')
    }
  }

  subscribe(channel: RealtimeChannel, id: string): void {
    if (!channel || !id) return
    const key = `${channel}:${id}`
    const current = this.channels.get(key)
    if (current) {
      current.count += 1
      return
    }
    this.channels.set(key, { channel, id, count: 1 })
    this.client?.subscribe(channel, id)
  }

  unsubscribe(channel: RealtimeChannel, id: string): void {
    const key = `${channel}:${id}`
    const current = this.channels.get(key)
    if (!current) return
    current.count -= 1
    if (current.count > 0) return
    this.channels.delete(key)
    this.client?.unsubscribe(channel, id)
  }

  sendTyping(conversationId: string, typing: boolean): void {
    if (!conversationId || this.status !== 'ready') return
    this.client?.setTyping(conversationId, typing)
  }

  addFrameListener(listener: RealtimeFrameListener): () => void {
    this.frameListeners.add(listener)
    return () => {
      this.frameListeners.delete(listener)
    }
  }

  addStatusListener(listener: RealtimeStatusListener): () => void {
    this.statusListeners.add(listener)
    return () => {
      this.statusListeners.delete(listener)
    }
  }

  /** Fires after a socket reconnect reaches `ready` again. */
  addResyncListener(listener: RealtimeResyncListener): () => void {
    this.resyncListeners.add(listener)
    return () => {
      this.resyncListeners.delete(listener)
    }
  }

  private getAccessToken(): string | null {
    try {
      return this.options.getAccessToken?.() ?? null
    } catch {
      return null
    }
  }

  private ensureClient(): RealtimeClient {
    if (this.client) return this.client
    this.client = new RealtimeClient({
      url: this.options.url,
      getAccessToken: () => this.getAccessToken(),
      socketFactory: this.options.socketFactory,
      reconnectBaseMs: this.options.reconnectBaseMs,
      reconnectMaxMs: this.options.reconnectMaxMs,
      authTimeoutMs: this.options.authTimeoutMs,
      heartbeatMs: this.options.heartbeatMs,
      random: this.options.random,
      onFrame: (frame) => this.handleFrame(frame),
      onStatus: (status) => this.handleStatus(status),
      onAuthError: () => {
        // Auth closes are terminal until the session/token changes; the
        // provider restarts on the next authenticated session.
        this.started = false
      },
    })
    return this.client
  }

  private handleFrame(frame: ServerFrame): void {
    // The hub attaches `user:{id}` automatically after auth (REALTIME.md §3);
    // notification frames therefore need no client subscribe.
    if (frame.type === 'ready') this.readyUserId = frame.userId

    if (frame.type === 'notification') {
      const id = notificationId(frame.notification)
      if (!id || this.seenNotificationIds.has(id)) return
      this.seenNotificationIds.add(id)
      this.notificationOrder.push(id)
      if (this.notificationOrder.length > MAX_TRACKED_NOTIFICATIONS) {
        const oldest = this.notificationOrder.shift()
        if (oldest) this.seenNotificationIds.delete(oldest)
      }
    }

    for (const listener of [...this.frameListeners]) listener(frame)
  }

  private handleStatus(status: RealtimeStatus): void {
    const wasReady = this.status === 'ready'
    this.status = status
    if (status === 'closed') {
      this.started = false
    }
    if (status === 'ready' && !wasReady) {
      const isReconnect = this.hasConnected
      this.hasConnected = true
      if (isReconnect) {
        for (const listener of [...this.resyncListeners]) listener()
      }
    }
    for (const listener of [...this.statusListeners]) listener(status)
  }

  private setStatus(status: RealtimeStatus): void {
    if (this.status === status) return
    this.status = status
    for (const listener of [...this.statusListeners]) listener(status)
  }
}

function notificationId(value: unknown): string | null {
  if (!value || typeof value !== 'object') return null
  const id = (value as Record<string, unknown>).id
  return typeof id === 'string' && id.trim() ? id : null
}
